import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { api } from "@constructionautomation/backend";
import type { Id } from "@constructionautomation/backend/convex/_generated/dataModel";
import { orgRoleHasCapability, requiresProjectAssignment, type OrgRole } from "@constructionautomation/backend/convex/lib/orgRoles";
import { MultiSelect } from "@/components/ui/multi-select";

// Mirrors MAX_INVITE_PROJECTS in convex/invitationProjectAssignments.ts.
const MAX_INVITE_PROJECTS = 25;

/**
 * Initial projects for a member or reviewer invitation. Admins see every project, so the picker is
 * replaced by a note for them; the server rejects project choices for those roles too.
 */
export function InviteProjectPicker({
  role,
  selected,
  onChange,
  disabled,
}: {
  role: OrgRole;
  selected: Id<"projects">[];
  onChange: (projectIds: Id<"projects">[]) => void;
  disabled?: boolean;
}) {
  const needsAssignment = requiresProjectAssignment(role);
  const { data: projects } = useQuery(convexQuery(api.projects.listAll, needsAssignment ? {} : "skip"));
  const sections = useMemo(
    () => [
      {
        id: "projects",
        label: "Projects",
        options: (projects ?? []).map((project) => ({ value: project._id, label: project.name })),
      },
    ],
    [projects]
  );

  if (!needsAssignment) {
    return <p className="text-sm text-muted-foreground">Admins can open every project.</p>;
  }
  if (projects !== undefined && projects.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        Create a project first to give them access when they join. You can also add them to projects later.
      </p>
    );
  }

  const atLimit = selected.length >= MAX_INVITE_PROJECTS;
  return (
    <div className="space-y-1.5">
      <MultiSelect
        sections={sections}
        selected={selected}
        onSelect={(value) => {
          if (!atLimit) onChange([...selected, value as Id<"projects">]);
        }}
        onRemove={(value) => onChange(selected.filter((projectId) => projectId !== value))}
        onSelectAll={() => onChange(sections[0]!.options.slice(0, MAX_INVITE_PROJECTS).map((option) => option.value as Id<"projects">))}
        onDeselectAll={() => onChange([])}
        placeholder="Add to projects (optional)..."
        triggerLabel="Projects"
        triggerAriaLabel="Projects to add them to when they accept"
        disabled={disabled || projects === undefined}
        triggerClassName="h-10 w-full border-2 border-border text-sm sm:w-auto"
      />
      <p className="text-xs text-muted-foreground">
        {orgRoleHasCapability(role, "edit_work")
          ? "Members only see projects they're added to."
          : "Reviewers only see projects they're added to."}{" "}
        Access starts when they accept the invitation{atLimit ? `; up to ${MAX_INVITE_PROJECTS} projects` : ""}.
      </p>
    </div>
  );
}
